import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useBillingStatus } from "../hooks/useBillingStatus.js";
import { getIntegrations, createIntegration, updateIntegration, deleteIntegration, testIntegration } from "../api.js";
import type { Integration } from "@tuit/shared-types";
import styles from "../styles/pages/SettingsPage.module.css";

const TYPES: Array<{ value: "slack" | "discord" | "teams"; label: string }> = [
  { value: "slack", label: "Slack" },
  { value: "discord", label: "Discord" },
  { value: "teams", label: "Microsoft Teams" },
];

export function IntegrationsPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { status: billing } = useBillingStatus();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [name, setName] = useState("");
  const [type, setType] = useState<"slack" | "discord" | "teams">("slack");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isLoaded && !isSignedIn) navigate({ to: "/" });
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!isSignedIn) return;
    getIntegrations().then(setIntegrations).catch(() => {});
  }, [isSignedIn]);

  if (!isLoaded || !isSignedIn) return null;

  const canIntegrations = billing?.capabilities.canWebhooks ?? false;

  function flash(text: string) { setMsg(text); setTimeout(() => setMsg(null), 3500); }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !webhookUrl.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const intg = await createIntegration({ name: name.trim(), type, webhookUrl: webhookUrl.trim() });
      setIntegrations((prev) => [...prev, intg]);
      setName("");
      setWebhookUrl("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add integration");
    } finally {
      setAdding(false);
    }
  }

  async function handleToggle(intg: Integration) {
    setBusyId(intg.id);
    try {
      const updated = await updateIntegration(intg.id, { enabled: !intg.enabled });
      setIntegrations((prev) => prev.map((i) => (i.id === intg.id ? updated : i)));
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(id: string) {
    setBusyId(id);
    try {
      await deleteIntegration(id);
      setIntegrations((prev) => prev.filter((i) => i.id !== id));
    } finally {
      setBusyId(null);
    }
  }

  async function handleTest(id: string) {
    setBusyId(`test_${id}`);
    try {
      await testIntegration(id);
      flash("Test message sent (check console in dev)");
    } catch {
      flash("Test failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className={styles.page}>
      <nav className={styles.topbar}>
        <Link to="/profile" className={styles.back}>← Profile</Link>
      </nav>
      <div className={styles.inner}>
        <h1 className={styles.heading}>Integrations</h1>
        <p className={styles.sub}>Send new posts to Slack, Discord, or Microsoft Teams channels.</p>

        {!canIntegrations ? (
          <div className={styles.upsell}>
            <p>Integrations are available on the <strong>Pro</strong> plan.</p>
            <Link to="/premium" className={styles.upsellLink}>Upgrade now →</Link>
          </div>
        ) : (
          <>
            <form className={styles.card} onSubmit={handleAdd}>
              <div className={styles.fieldGroup}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Name (e.g. #tech-news)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <div className={styles.inlineFields}>
                  <select className={styles.inputNarrow} value={type} onChange={(e) => setType(e.target.value as "slack" | "discord" | "teams")}>
                    {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input
                    type="url"
                    className={styles.input}
                    placeholder="Incoming webhook URL"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                  />
                </div>
                <button type="submit" className={styles.primaryBtn} disabled={adding || !name.trim() || !webhookUrl.trim()}>
                  {adding ? "Adding…" : "Add integration"}
                </button>
              </div>
              {error && <p className={styles.error}>{error}</p>}
            </form>

            {integrations.length > 0 && (
              <ul className={styles.list}>
                {integrations.map((intg) => (
                  <li key={intg.id} className={styles.listItem}>
                    <span className={`${styles.dot} ${intg.enabled ? styles.dotGreen : styles.dotGray}`} />
                    <div className={styles.listItemMain}>
                      <span className={styles.listItemText}>{intg.name}</span>
                      <span className={styles.listItemMeta}>{TYPES.find((t) => t.value === intg.type)?.label}</span>
                    </div>
                    <div className={styles.rowActions}>
                      <button type="button" className={styles.smallBtn} onClick={() => handleTest(intg.id)} disabled={busyId === `test_${intg.id}`}>Test</button>
                      <button type="button" className={styles.smallBtn} onClick={() => handleToggle(intg)} disabled={busyId === intg.id}>
                        {intg.enabled ? "Disable" : "Enable"}
                      </button>
                      <button type="button" className={styles.deleteBtn} onClick={() => handleDelete(intg.id)} disabled={busyId === intg.id}>Remove</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {integrations.length === 0 && <p className={styles.hint}>No integrations yet.</p>}
          </>
        )}

        {msg && <p className={styles.flash}>{msg}</p>}
      </div>
    </div>
  );
}
